import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { FeatureModel, PackageCardModel } from '@vp/digital-common-data-model-lib-ubik'
import { isProduction } from '@vp/digital-environment-lib-ubik'
import { LinkFactory, TokenReplacer } from '@vp/digital-token-replacer-lib-ubik'
import { WIX_PRIVACY_POLICY_PAGER_URL, WIX_TERMS_OF_USE_PAGER_URL } from '@vp/digital-url-service-lib-ubik'
import { analyticsDataRegistry } from '@vp/digital-tracking-lib-ubik'

import {
  BoundedContent,
  Box,
  BulletList,
  BulletListContent,
  Button,
  Column,
  Divider,
  FlexBox,
  GridContainer,
  H1,
  Hidden,
  Icon,
  List,
  ListItem,
  Popover,
  PopoverContent,
  PopoverLauncher,
  Row,
  Spinner,
  ToggleSwitch,
  ToggleSwitchOffLabel,
  ToggleSwitchOnLabel,
  Typography,
  Visible,
} from '@vp/swan'
import { getWixVistaBillingUpgradePlanUrl } from '@vp/digital-url-service-lib-ubik'
import { getClientEnvironment } from '@vp/digital-environment-lib-ubik'
import { useLogger, useStyles, useIdentity } from '@vp/ubik-context'
import { useSignedInAction } from '../../common/helpers/useSignedInAction'

import {
  HERO_CTA_DETAIL,
  PLANS_HERO_CTA_DATA_SECTION_LABEL,
  trackPlansHeroCTAClick,
  trackCreateFreeVistaWixWebsitePricingClick,
} from '../../common/WixNewPlansAnalytics'

import '@vp/digital-faq-lib-ubik/dist/index.css'
import { redirect } from '../../helpers/urlsHelper'
import { freePackage, isNewPlan } from '../../helpers/packageTierMapper'
import WixNewPackageGridHeader from '../../common/wixNewPackageGrid/WixNewPackageGridHeader'
import WixNewPackageGrid from '../../common/wixNewPackageGrid/WixNewPackageGrid'
import WebsitesFAQs from '../../common/WebsitesFAQs/WebsitesFAQs'
import PaymentsControlContainer from '../../common/newPaymentsControlContainer/PaymentsControlContainer'
import SalesBanner from '../../common/salesBanner/SalesBanner'
import { initialize, waitTillAvailable, isFeatureEnabled } from '@vp/ab-reader'

import styles, { stylesheet } from './WixNewAnonymousPlansPage.scss'
import { SubscriptionOptionsEnum, WixPageAnalyticsContext } from '../../common/helpers/WixPageAnalyticsProvider'
import { getPartnerAccountSiteIds } from '../../common/helpers/partnerAccountHelpers'
import BackBtn from '../../common/components/BackBtn'

const WixNewAnonymousPlansPage = (pageContext: any) => {
  const logger = useLogger()
  useStyles(stylesheet)
  const [isShorterJourneyEnabled, setIsShorterJourneyEnabled] = useState(false)
  const [siteId, setSiteId] = useState('')
  const [plansCtasIndex, setPlansCtasIndex] = useState([2])
  const { wixPlans } = analyticsDataRegistry
  const { trackProductList, trackToggleClickedEvent } = useContext(WixPageAnalyticsContext)
  const {
    mpv: { version: productVersion },
    urlWixPartnerGetStartedTnCAcceptedFromPlansUrls,
  } = pageContext

  const content = {
    ...pageContext.pageData,
    packageGridPlans: pageContext.packageGridPlans,
  }
  const urlWixPartnerGetStartedTnCAcceptedUrl = isProduction()
    ? urlWixPartnerGetStartedTnCAcceptedFromPlansUrls.prod
    : urlWixPartnerGetStartedTnCAcceptedFromPlansUrls.qa

  const [isGetStartedLoading, setIsGetStartedLoading] = useState(false)
  const [loadingPlans, setLoadingPlans] = useState([''])
  const [isBilledYearly, setIsBilledYearly] = useState(true)

  const plansFeaturesSectionRef = useRef(null)
  const [isLoadingPartnerAccount, setIsLoadingPartnerAccount] = useState(true)
  const { auth, isSignedIn } = useIdentity()
  const canonicalId = auth?.getCanonicalId()

  // const isMobile = useScreenClass() === 'xs'
  const isMobile = false

  // get override params for testing
  let isOverrideSales: boolean = false
  useEffect(() => {
    setTimeout(() => {
      trackProductList(wixPlans, pageContext.mpv.locale)
    }, 100)

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setLoadingPlans([])
        setIsGetStartedLoading(false)
      }
    }

    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  if (!isProduction() && typeof window !== 'undefined' && window.location?.search) {
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    isOverrideSales = urlParams?.get('sales') === 'true' && isBilledYearly
  }

  const addPlanToLoadingPlans = (planKey?: string) => {
    if (planKey) {
      const newLoadingPlans = new Set([...loadingPlans])
      newLoadingPlans.add(planKey)
      setLoadingPlans(Array.from(newLoadingPlans))
      setIsGetStartedLoading(isGetStartedLoading || planKey == freePackage)
    }
  }

  const removePlanFromLoadingPlans = (planKey?: string) => {
    if (planKey) {
      const newLoadingPlans = loadingPlans.filter((plan: string) => plan !== planKey)
      setLoadingPlans(newLoadingPlans)
      setIsGetStartedLoading(isGetStartedLoading && !(planKey == freePackage))
    }
  }

  const handleCompareTableScroll = () => {
    plansFeaturesSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  const handleWixGetStartedNavigation = async () => {
    try {
      logger.debug('Clicking Wix Anonymous Plans Page Get Started to Wix GetStarted Navigate')
      let url = urlWixPartnerGetStartedTnCAcceptedUrl
      if (isMobile) {
        url += '&deviceType=mobile'
      }
      addPlanToLoadingPlans(freePackage)
      await redirect(url)
    } catch (error: any) {
      removePlanFromLoadingPlans(freePackage)
      logger.error('Error handling Get Started Navigation:', error)
    }
  }

  const handleChoosePlanNavigation = async (planKey?: string) => {
    try {
      addPlanToLoadingPlans(planKey)

      window.location.href = getWixVistaBillingUpgradePlanUrl(
        pageContext.locale,
        siteId,
        'anonymous-plan-picker-page',
        getClientEnvironment(),
        'anonymous-plan-picker-page',
      )
    } catch (error: any) {
      removePlanFromLoadingPlans(planKey)
      logger.error('Error handling Choose Plan Navigation:', error)
    }
  }

  const fetchPartnerAccount = useCallback(async (locale: string) => {
    try {
      initialize()
      const data = await getPartnerAccountSiteIds(locale, logger, 'anonymous-plan-picker-page')
      const websitesCount = data?.length ?? 0
      if (websitesCount === 1) {
        setSiteId(data[0])
      }
      if (websitesCount == 0) {
        logger.debug('Could not run experiment due to missing partner account data', { data })
        throw new Error('Could not run experiment due to missing customer account')
      }
      const available = await waitTillAvailable(1000)
      if (!available) {
        logger.debug('Could not run experiment because ab-reader is not available', { available })
        throw new Error('Could not run experiment because ab-reader is not available')
      }
      const isShorterJourneyFeatureEnabled = isFeatureEnabled('vxw_shorter_freemium_to_premium')
      setIsShorterJourneyEnabled(isShorterJourneyFeatureEnabled)
      if (isShorterJourneyFeatureEnabled) {
        // Show new experience
        setIsShorterJourneyEnabled(true)
        setPlansCtasIndex(
          content.packageGridPlans.packages.map((plan: PackageCardModel) => {
            if (plan.key === freePackage) return 2
            return 0
          }),
        )
      } else {
        //show default / control experience
        setIsShorterJourneyEnabled(false)
        setPlansCtasIndex([2])
      }
    } catch (error) {
      logger.warn('Failed to fetch partner account group:', error)
      setIsShorterJourneyEnabled(false)
      setPlansCtasIndex([2])
    } finally {
      setIsLoadingPartnerAccount(false)
    }
  }, [])

  const [, , initiateFetchPartnerAccount] = useSignedInAction<string, void>(fetchPartnerAccount, 'fetch-partner-account-after-sign-in')

  useEffect(() => {
    if (!canonicalId) return
    if (!isSignedIn) {
      setIsShorterJourneyEnabled(false)
      setPlansCtasIndex([2])
      setIsLoadingPartnerAccount(false)
      return
    }
    initiateFetchPartnerAccount(pageContext.locale)
  }, [canonicalId, isSignedIn, pageContext.locale, initiateFetchPartnerAccount])

  const refactoredPackageGridPlans = {
    ...content.packageGridPlans,
    packages: content.packageGridPlans.packages.map((plan: any) => ({
      ...plan,
      isLoading: isLoadingPartnerAccount && plan.key !== freePackage,
      selectPackageButtons: plan.selectPackageButtons.map((button: any, index: any) => ({
        ...button,
        isLoading: isLoadingPartnerAccount && plan.key !== freePackage && index === 0,
      })),
    })),
  }

  const onPackageSelected = (plan: PackageCardModel) => {
    // add navigation event
    if (plan.key === freePackage) {
      trackCreateFreeVistaWixWebsitePricingClick(urlWixPartnerGetStartedTnCAcceptedUrl)
      handleWixGetStartedNavigation()
    } else {
      handleChoosePlanNavigation(plan.key)
    }
  }

  return (
    <>
      <BoundedContent>
        {/* back button */}
        {content.hero.ctaName && (
          <Visible md lg xl>
            <BackBtn ctaName={content.hero.ctaName} className={styles.wixNewPlansPageBackButton} />
          </Visible>
        )}

        {/* hero */}
        {content.hero && (
          <GridContainer component="section" mt="7">
            <Row>
              <Column span={12}>
                <Box className={styles.wixNewPlansPageHeroContainer}>
                  <H1 fontSize="x3large">{content.hero.title}</H1>
                  <Typography component="p" fontSize="small" fontWeight="bold">
                    {content.hero.descriptionLong}
                  </Typography>
                  <Button
                    mt="5"
                    skin="primary"
                    onClick={() => {
                      trackPlansHeroCTAClick(urlWixPartnerGetStartedTnCAcceptedUrl)
                      handleWixGetStartedNavigation()
                    }}
                    data-section={PLANS_HERO_CTA_DATA_SECTION_LABEL}
                    data-position="1"
                    data-translation={HERO_CTA_DETAIL}
                  >
                    {content.hero.secondaryCtaName}

                    {isGetStartedLoading && <Spinner accessibleText="Loading..." />}
                  </Button>

                  <List skin="minimal" mt="5" p={0} className={styles.wixNewPlansPageHeroFeatures}>
                    {content.hero.heroFeatureGroup.features.map((item: FeatureModel) => (
                      <ListItem key={item.description} fontSize="xsmall" fontWeight="bold" mx="3" p={0}>
                        <Icon iconType="check" size="20p" skin="standard" mr="2" />
                        {item.description}
                      </ListItem>
                    ))}
                  </List>
                  <Typography mt="5" textColor="standard" fontSize="xsmall">
                    <TokenReplacer
                      template={pageContext.pageData.hero.secondaryCtaDescription}
                      replacements={{
                        link: LinkFactory({
                          wixTermsOfUsePagerUrl: WIX_TERMS_OF_USE_PAGER_URL,
                          wixPrivacyPolicyPagerUrl: WIX_PRIVACY_POLICY_PAGER_URL,
                        }),
                      }}
                      convertMarkdown
                    />
                  </Typography>
                </Box>
              </Column>
            </Row>
          </GridContainer>
        )}

        {/* divider */}
        <GridContainer mt="7">
          <Row>
            <Column span={12}>
              <Hidden xs>
                <Divider />
              </Hidden>
            </Column>
          </Row>
        </GridContainer>
      </BoundedContent>

      <SalesBanner content={content.salesBanner} locale={pageContext.locale} />

      <BoundedContent mt="5">
        {/* billing cycle toggle */}
        {content.billedYearlyToggle && (
          <GridContainer component="section">
            <Row>
              <Column span={12}>
                <Box component="section" textAlign="right">
                  <ToggleSwitch
                    mr={0}
                    activated={isBilledYearly}
                    onRequestActivatedChange={() => {
                      setIsBilledYearly(!isBilledYearly)
                      trackToggleClickedEvent(wixPlans, isBilledYearly ? SubscriptionOptionsEnum.MONTHLY : SubscriptionOptionsEnum.ANNUALLY)
                    }}
                  >
                    <ToggleSwitchOnLabel>{content.billedYearlyToggle.title}</ToggleSwitchOnLabel>
                    <ToggleSwitchOffLabel textColor="subtle">{content.billedYearlyToggle.title}</ToggleSwitchOffLabel>
                  </ToggleSwitch>

                  <Typography fontSkin="footnote-bold" textColor="promo" mt={3}>
                    {isBilledYearly ? (
                      content.billedYearlyToggle.description
                    ) : (
                      <Popover className={styles.wixNewPlansPageBillingTogglePopover}>
                        <PopoverLauncher>
                          <Typography textColor="standard" fontSize="xsmall" component="span" fontWeight="bold">
                            {content.billedYearlyToggle.descriptionLong}{' '}
                            <Typography tabIndex={0} component="span" textColor="error" className={styles.wixNewPlansPageBillingToggleDescriptionError}>
                              {content.billedYearlyToggle.footer}
                            </Typography>
                          </Typography>
                        </PopoverLauncher>
                        <PopoverContent className={styles.wixNewPlansPageBillingTogglePopoverContent}>
                          <Typography component="p" fontSize="small" m={0}>
                            {content.billedYearlyToggle.heroFeatureGroup.title}
                          </Typography>
                          <Typography component="p" fontSize="small" m={0} fontWeight="bold">
                            {content.billedYearlyToggle.heroFeatureGroup.description}
                          </Typography>
                          <BulletList mt="5">
                            {content.billedYearlyToggle.heroFeatureGroup.features.map((feature: any) => (
                              <ListItem key={feature.title} p={0} mb="2">
                                <BulletListContent component="p" className="text-size-6" fontSize="small" m={0}>
                                  <Icon iconType="toggleOff" mr="3" size="16p" />
                                  {feature.title}
                                </BulletListContent>
                              </ListItem>
                            ))}
                          </BulletList>
                        </PopoverContent>
                      </Popover>
                    )}
                  </Typography>
                </Box>
              </Column>
            </Row>
          </GridContainer>
        )}

        {/* plans header */}
        {content.packageGridPlans && (
          <GridContainer component="section" mt="5">
            <Row>
              <Column span={12}>
                <WixNewPackageGridHeader
                  packageGridPlans={refactoredPackageGridPlans}
                  plansCtasIndex={plansCtasIndex}
                  selectPackageCallback={onPackageSelected}
                  defaultSkin={isShorterJourneyEnabled ? 'primary' : 'secondary'}
                  buttonsPosition={'top'}
                  productVersion={productVersion}
                  highlightedPlans={['Business']}
                  isBilledYearly={isBilledYearly}
                  overrideSales={isOverrideSales}
                  accessibility={{
                    translations: content.accessibility,
                  }}
                  loadingPlans={loadingPlans}
                />
              </Column>
            </Row>
          </GridContainer>
        )}

        {/* compare plan features cta */}
        {content.comparePlans && (
          <GridContainer component="section" mt="7">
            <Row>
              <Column span={12}>
                <FlexBox justifyContent="center">
                  <Button m={0} skin="secondary" onClick={handleCompareTableScroll} size={isMobile ? 'mini' : 'standard'}>
                    {content.comparePlans.title}
                  </Button>
                </FlexBox>
              </Column>
            </Row>
          </GridContainer>
        )}

        {/* payments methods */}
        {content.payments && (
          <GridContainer component="section" mt="7">
            <Row>
              <Column span={12}>
                <PaymentsControlContainer content={content.payments} />
              </Column>
            </Row>
          </GridContainer>
        )}

        {/* divider */}
        <GridContainer mt={8}>
          <Row>
            <Column span={12}>
              <Divider />
            </Column>
          </Row>
        </GridContainer>

        {/* plans features */}
        {content.packageGridPlans && (
          <GridContainer component="section" ref={plansFeaturesSectionRef}>
            <Row>
              <Column span={12}>
                <WixNewPackageGrid
                  packageGridPlans={{
                    ...content.packageGridPlans,
                    packages: content.packageGridPlans.packages.filter((plan: PackageCardModel) => isNewPlan(plan.key ?? '')),
                  }}
                  productVersion={productVersion}
                  highlightedPlans={['Business']}
                  isBilledYearly={isBilledYearly}
                  overrideSales={isOverrideSales}
                />
              </Column>
            </Row>
          </GridContainer>
        )}
      </BoundedContent>
      {/* faq */}
      {content.faqs && (
        <BoundedContent as="article" mt={{ xs: 8, sm: 9, md: 9, lg: 10, xl: 10 }} py={{ xs: 8, sm: 9, md: 9, lg: 10, xl: 10 }} backgroundColor="warm-white">
          <WebsitesFAQs content={content.faqs} collapsed />
        </BoundedContent>
      )}
    </>
  )
}

export default WixNewAnonymousPlansPage
